import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Award, MapPin, Star, Calendar, Filter, X, Stethoscope, Users, Clock, DollarSign } from 'lucide-react';
import PublicHeader from '../components/PublicHeader';
import { useTranslation } from '../hooks/useTranslation';
import { Skeleton } from '../components/ui/skeleton';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function Doctors() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    city: '',
    state: '',
    specialization: '',
    experience: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    cities: [],
    states: [],
    specializations: []
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchDoctors();
  }, []);

  const fetchDoctors = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/public/doctors`);
      if (response.ok) {
        const data = await response.json();
        setDoctors(data.doctors || []);
        
        // Extract unique filter options
        const allDoctors = data.doctors || [];
        setFilterOptions({
          cities: [...new Set(allDoctors.map(d => d.city).filter(Boolean))].sort(),
          states: [...new Set(allDoctors.map(d => d.state).filter(Boolean))].sort(),
          specializations: [...new Set(allDoctors.map(d => d.specialization).filter(Boolean))].sort()
        });
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const getDoctorSlug = (name) => {
    return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/^dr-/, '');
  };

  // Filter doctors
  const filteredDoctors = doctors.filter(doctor => {
    if (filters.city && doctor.city !== filters.city) return false;
    if (filters.state && doctor.state !== filters.state) return false;
    if (filters.specialization && doctor.specialization !== filters.specialization) return false;
    if (filters.experience) {
      const years = doctor.experience_years || 0;
      if (filters.experience === '5+' && years < 5) return false;
      if (filters.experience === '10+' && years < 10) return false;
      if (filters.experience === '15+' && years < 15) return false;
      if (filters.experience === '20+' && years < 20) return false;
    }
    return true;
  });

  const clearFilters = () => {
    setFilters({ city: '', state: '', specialization: '', experience: '' });
  };

  const activeFiltersCount = Object.values(filters).filter(v => v !== '').length;

  // Loading skeleton
  const DoctorSkeleton = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-start gap-4">
        <Skeleton className="w-20 h-20 rounded-full" />
        <div className="flex-1">
          <Skeleton className="h-6 w-40 mb-2" />
          <Skeleton className="h-4 w-24 mb-2" />
          <Skeleton className="h-4 w-48 mb-2" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-4">
        <Skeleton className="h-16" />
        <Skeleton className="h-16" />
        <Skeleton className="h-16" />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-[hsl(193_95%_95%)] to-[hsl(210_40%_98%)]">
      <PublicHeader currentPage="doctors" />
      
      {/* Hero Section */}
      <section className="pt-28 pb-12 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-[hsl(var(--foreground))] mb-4" data-testid="doctors-title">
              {t('doctors.heroTitle')} <span className="text-[hsl(var(--primary))]">{t('doctors.heroTitleHighlight')}</span>
            </h1>
            <p className="text-lg text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto">
              {t('doctors.heroSubtitle')}
            </p>
          </div>

          {/* Filter Toggle Button (Mobile) */}
          <div className="md:hidden mb-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white rounded-lg shadow-sm border"
            >
              <Filter className="w-5 h-5" />
              <span>Filters</span>
              {activeFiltersCount > 0 && (
                <span className="bg-[hsl(var(--primary))] text-white text-xs px-2 py-0.5 rounded-full">
                  {activeFiltersCount}
                </span>
              )}
            </button>
          </div>

          {/* Filters Section */}
          <div className={`bg-white rounded-xl shadow-sm border border-gray-100 p-4 md:p-6 mb-8 ${showFilters ? 'block' : 'hidden md:block'}`}>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <select
                  value={filters.city}
                  onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-transparent"
                  data-testid="filter-city"
                >
                  <option value="">All Cities</option>
                  {filterOptions.cities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <select
                  value={filters.state}
                  onChange={(e) => setFilters({ ...filters, state: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-transparent"
                  data-testid="filter-state"
                >
                  <option value="">All States</option>
                  {filterOptions.states.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Specialization</label>
                <select
                  value={filters.specialization}
                  onChange={(e) => setFilters({ ...filters, specialization: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-transparent"
                  data-testid="filter-specialization"
                >
                  <option value="">All Specializations</option>
                  {filterOptions.specializations.map(spec => (
                    <option key={spec} value={spec}>{spec}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Experience</label>
                <select
                  value={filters.experience}
                  onChange={(e) => setFilters({ ...filters, experience: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-transparent"
                  data-testid="filter-experience"
                >
                  <option value="">Any Experience</option>
                  <option value="5+">5+ years</option>
                  <option value="10+">10+ years</option>
                  <option value="15+">15+ years</option>
                  <option value="20+">20+ years</option>
                </select>
              </div>
              {activeFiltersCount > 0 && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1 px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg self-end"
                >
                  <X className="w-4 h-4" />
                  Clear All
                </button>
              )}
            </div>
          </div>

          {/* Results Count */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-gray-600">
              Showing <span className="font-semibold text-gray-900">{filteredDoctors.length}</span> doctors
            </p>
          </div>

          {/* Doctors Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => <DoctorSkeleton key={i} />)}
            </div>
          ) : filteredDoctors.length === 0 ? (
            <div className="text-center py-12">
              <Stethoscope className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No doctors found</h3>
              <p className="text-gray-500">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredDoctors.map((doctor, index) => (
                <div
                  key={doctor._id || index}
                  onClick={() => navigate(`/doctors/${doctor.user_id || getDoctorSlug(doctor.name)}`)}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-lg transition-all cursor-pointer group"
                  data-testid={`doctor-card-${index}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-20 h-20 bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(193_80%_50%)] rounded-full flex items-center justify-center text-white text-3xl shadow-lg">
                      {doctor.name?.charAt(0) === 'D' ? '👨‍⚕️' : '👩‍⚕️'}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 group-hover:text-[hsl(var(--primary))] transition-colors">
                        {doctor.name}
                      </h3>
                      <div className="flex items-center gap-1 text-[hsl(var(--primary))] mt-0.5">
                        <Stethoscope className="w-4 h-4" />
                        <span className="font-medium text-sm">{doctor.specialization}</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{doctor.hospital}</p>
                      {(doctor.city || doctor.state) && (
                        <div className="flex items-center gap-1 text-gray-500 text-sm mt-1">
                          <MapPin className="w-3 h-3" />
                          <span>{[doctor.city, doctor.state].filter(Boolean).join(', ')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Qualification */}
                  <div className="mt-3 flex items-center gap-2">
                    <Award className="w-4 h-4 text-yellow-500" />
                    <span className="text-sm text-gray-600 truncate">{doctor.qualification}</span>
                  </div>
                  
                  {/* Stats */}
                  <div className="mt-4 grid grid-cols-3 gap-3 pt-4 border-t border-gray-100">
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        <span className="font-bold text-gray-900">{doctor.rating || 'N/A'}</span>
                      </div>
                      <span className="text-xs text-gray-500">Rating</span>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Clock className="w-4 h-4 text-blue-500" />
                        <span className="font-bold text-gray-900">{doctor.experience_years || 0}</span>
                      </div>
                      <span className="text-xs text-gray-500">Years Exp</span>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Users className="w-4 h-4 text-green-500" />
                        <span className="font-bold text-gray-900">{doctor.patients}</span>
                      </div>
                      <span className="text-xs text-gray-500">Patients</span>
                    </div>
                  </div>
                  
                  {/* Languages & Fee */}
                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex flex-wrap gap-1">
                      {(doctor.languages || []).slice(0, 2).map((lang, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                          {lang}
                        </span>
                      ))}
                      {(doctor.languages || []).length > 2 && (
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                          +{doctor.languages.length - 2}
                        </span>
                      )}
                    </div>
                    {doctor.consultation_fee > 0 && (
                      <div className="flex items-center gap-1 text-green-600 font-semibold text-sm">
                        <DollarSign className="w-4 h-4" />
                        <span>₹{doctor.consultation_fee}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
